import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import DataSource from 'devextreme/data/data_source';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UnitViewModel } from '../../../../../shared/models/ocl';
import { Client } from '../../../models/order-entry/client';
import { PatientSearch } from '../../../models/order-entry/patient';
import { ClientService, PatientService } from '../../../services';

@Component({
  selector: 'app-client-physician-selector-dialog',
  templateUrl: './client-physician-selector-dialog.component.html',
  styleUrls: ['./client-physician-selector-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientPhysicianSelectorDialogComponent implements OnInit, OnDestroy {
  @Input() public popupVisible = false;
  @Input() public subtitle = '';
  @Input() public selectedPatient?: PatientSearch = {} as PatientSearch;
  @Output() public closeEvent = new EventEmitter();
  public clientPhysicianDialogForm: FormGroup = this.fb.group({
    client: [null, [Validators.required]],
    physician: [null],
    unit: [null],
    room: [null],
    bed: [''],
  });
  public role?: string;
  public units: BehaviorSubject<DataSource> = new BehaviorSubject<DataSource>(null as unknown as DataSource);
  public selectRooms: BehaviorSubject<DataSource> = new BehaviorSubject<DataSource>(null as unknown as DataSource);
  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    public patientService: PatientService,
    public clientService: ClientService,
    private ref: ChangeDetectorRef,
  ) {
  }

  public convertToFormControl(absCtrl: AbstractControl | null): FormControl {
    return absCtrl as FormControl;
  }

  public ngOnInit() {
    this.closeEvent.pipe(takeUntil(this.onDestroy$)).subscribe(patient => {
      if (patient) {
        this.popupVisible = false;
      }
      this.ref.detectChanges();
    });

    this.clientPhysicianDialogForm.get('client')?.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((client: Client) => {
        this.clientPhysicianDialogForm.get('unit')?.setValue(undefined);
        this.clientPhysicianDialogForm.get('room')?.setValue(undefined);

        if (client && client.unitsCount > 0) {
          this.units.next(new DataSource({
            store: this.clientService.clientUnitsCustomStore(client.id, this.units),
          }));
          this.selectRooms.next(new DataSource({
            store: this.clientService.clientRoomsCustomStore(client.id, this.selectRooms),
            paginate: true,
            pageSize: 10,
          }));
        } else {
          this.selectRooms.next(null as unknown as DataSource);
          this.units.next(null as unknown as DataSource);
        }
        this.ref.detectChanges();
      });

    this.clientPhysicianDialogForm.get('unit')?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((unit: UnitViewModel | number) => {
        if (unit) {
          this.selectRooms.next(new DataSource({
            store: this.clientService.clientRoomsByUnitCustomStore(typeof unit === 'number' ? unit : unit.id, this.selectRooms),
            paginate: true,
            pageSize: 10,
          }));

          const selectedRoom = this.clientPhysicianDialogForm.get('room')?.value;
          const selectedUnit = this.clientPhysicianDialogForm.get('unit')?.value;
          if (selectedRoom?.unitId !== selectedUnit.id) {
            this.clientPhysicianDialogForm.get('room')?.setValue(undefined);
          }
        }
        this.ref.detectChanges();
      });

    this.clientPhysicianDialogForm.get('room')?.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(room => {
      if (room && !this.clientPhysicianDialogForm.get('unit')?.value) {
        this.clientPhysicianDialogForm.get('unit')?.setValue(room.unitId);
      }
      this.ref.detectChanges();
    });
  }

  public close(): void {
    this.clientPhysicianDialogForm.reset();
    this.closeEvent.emit();
  }

  public save() {
    if (this.clientPhysicianDialogForm.valid) {
      this.patientService.updatePatientClientPhysician({
        id: this.selectedPatient?.id,
        bed: this.clientPhysicianDialogForm.value.bed,
        clientId: this.clientPhysicianDialogForm.value.client.id,
        physicianId: this.clientPhysicianDialogForm.value.physician ? this.clientPhysicianDialogForm.value.physician.id : null,
        roomId: this.clientPhysicianDialogForm.value.room ? this.clientPhysicianDialogForm.value.room.id : null,
      })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(patient => {
          this.clientPhysicianDialogForm.reset();
          this.ref.detectChanges();
          this.closeEvent.emit(patient);
        });
    }
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
